import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./client";
import type { StyleProfile } from "./settings";

/** A brand the team produces work for; its style profile replaces the team
 *  default when a project selects the client. */
export interface Client {
  id: string;
  team_id: string;
  name: string;
  /** JSON-encoded StyleProfile, null when unset. */
  style_profile: string | null;
  created_at: string;
  updated_at: string;
}

export function parseClientStyle(client: Client): StyleProfile {
  if (!client.style_profile) return {};
  try {
    return JSON.parse(client.style_profile) as StyleProfile;
  } catch {
    return {};
  }
}

export const clientsKeys = {
  all: ["clients"] as const,
};

export function useClientsQuery() {
  return useQuery({
    queryKey: clientsKeys.all,
    queryFn: () => apiFetch<Client[]>("/clients"),
    staleTime: 30 * 1000,
  });
}

export interface ClientBody {
  name: string;
  style: StyleProfile;
}

export function useCreateClientMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ClientBody) =>
      apiFetch<Client>("/clients", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientsKeys.all });
    },
  });
}

export function useUpdateClientMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: ClientBody & { id: string }) =>
      apiFetch<Client>(`/clients/${id}`, {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientsKeys.all });
    },
  });
}

export function useDeleteClientMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ deleted: boolean }>(`/clients/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientsKeys.all });
    },
  });
}
